import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { SecurityTab } from "@/customer/account/SecurityTab";

import { ContactTab } from "./profile/ContactTab";
import { AddressesTab } from "./profile/AddressesTab";
import { PaymentMethodsTab } from "./profile/PaymentMethodsTab";
import { CoiTab } from "./profile/CoiTab";

// Phase 4a customer-portal FR-9 — Profile shell with four tabs.
// Mobile: tabs stack as a horizontal scroll-strip. Desktop: two-
// column with the tab list on the left.
//
// Phase 4u portal-profile-polish — design grade
// `.notes/design-grades/portal/03-profile.md` flagged the right-aligned
// "← Dashboard" link as reading like a "next" affordance. The back
// link now sits above the page heading (left-aligned, quieter zinc
// styling) so the brand-red eyebrow is the only red element competing
// for attention in the header row.
const TABS = [
  { key: "contact", label: "Contact" },
  { key: "addresses", label: "Address book" },
  { key: "payment", label: "Payment methods" },
  { key: "coi", label: "COI on file" },
  { key: "security", label: "Security" }
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function ProfilePage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.hash.replace("#", "") as TabKey;
  const [tab, setTab] = useState<TabKey>(
    TABS.some((t) => t.key === initialTab) ? initialTab : "contact"
  );

  function select(t: TabKey): void {
    setTab(t);
    navigate({ hash: `#${t}` }, { replace: true });
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3 border-b border-zinc-200 pb-4">
        <Link
          to="/portal"
          className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 transition hover:text-brand-red"
          data-testid="profile-back-link"
        >
          <span aria-hidden="true">←</span> Dashboard
        </Link>
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
            Account
          </p>
          <h1 className="font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl">
            Profile
          </h1>
          <span aria-hidden="true" className="inline-block h-0.5 w-10 bg-brand-red" />
        </div>
      </div>

      <div className="md:flex md:gap-6">
        <nav className="-mx-1 flex gap-1 overflow-x-auto md:mx-0 md:w-48 md:flex-col">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => select(t.key)}
              className={`min-h-[44px] whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition ${
                tab === t.key
                  ? "bg-flag-navy text-brand-white shadow-sm"
                  : "text-zinc-700 hover:bg-zinc-100 hover:text-brand-black"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="mt-2 flex-1 md:mt-0">
          {tab === "contact" ? <ContactTab /> : null}
          {tab === "addresses" ? <AddressesTab /> : null}
          {tab === "payment" ? <PaymentMethodsTab /> : null}
          {tab === "coi" ? <CoiTab /> : null}
          {tab === "security" ? <SecurityTab /> : null}
        </div>
      </div>
    </div>
  );
}
